import { Media } from '@/components/Media'
import { Link, useParams } from 'react-router'
import { formatMoney, fromMajor } from '@/lib/money'
import { Button, Card, Icon, Pill, Stack, Text } from '@/design-system'
import { PageHeader } from '@/app/layouts/PageHeader'
import { NotFoundScreen } from '@/features/system/NotFoundScreen'
import { DEFAULT_NIGHTS, HOTEL_CASHBACK, cityLabel, findStay } from './travel-data'
import checkout from '@/app/layouts/CheckoutLayout.module.css'
import { BRAND } from '@/brand/brand'

export function HotelDetailScreen() {
  const { id = '' } = useParams()
  const stay = findStay(id)
  if (!stay) return <NotFoundScreen />

  const nightly = stay.nightly
  const total = fromMajor(nightly * DEFAULT_NIGHTS, 'USD')
  const cashback = fromMajor(nightly * DEFAULT_NIGHTS * HOTEL_CASHBACK, 'USD')

  return (
    <>
      <PageHeader
        title={stay.name}
        subtitle={`${stay.area}, ${cityLabel(stay.city)} · 12–15 Oct · 2 guests · ${DEFAULT_NIGHTS} nights`}
        actions={
          <>
            <Button variant="ghost" size="md" iconStart="arrow_back" to="/travel/hotels">
              All stays
            </Button>
            <Button variant="ghost" size="md" iconStart="favorite">
              Save
            </Button>
          </>
        }
      />

      <div className={checkout.grid}>
        <Stack gap={3}>
          <Media
            src={stay.image}
            alt={`${stay.name}, ${stay.city}`}
            ratio={16 / 9}
            radius="lg"
            icon="hotel"
            eager
          />

          <Card tone="light">
            <Stack direction="row" align="center" gap={3} wrap>
              <Pill tone="accent">
                {stay.score.toFixed(1)} · {stay.verdict}
              </Pill>
              <Text variant="caption" tone="secondary">
                {stay.reviews} reviews from {BRAND.name} members
              </Text>
            </Stack>
            <Text variant="body" tone="secondary" className="pb-mt-4">
              {stay.blurb}
            </Text>
            <Stack direction="row" gap={2} wrap className="pb-mt-5">
              {stay.amenities.map((item) => (
                <Pill key={item} tone="neutral">
                  {item}
                </Pill>
              ))}
            </Stack>
          </Card>

          <Card tone="light">
            <Text variant="caption" weight="semibold">
              Rooms
            </Text>
            <Stack gap={0} className="pb-mt-3">
              {[
                ['Courtyard double', 'Queen bed · 28 m²', nightly],
                ['Garden suite', 'King bed · 42 m² · terrace', Math.round(nightly * 1.33)],
                ['Studio', 'Queen bed · kitchenette', Math.round(nightly * 0.88)],
              ].map(([name, detail, price]) => (
                <Link
                  key={String(name)}
                  to={`/travel/hotels/${stay.slug}/book`}
                  className={checkout.line}
                  style={{ width: '100%' }}
                >
                  <Stack grow gap={0} align="start">
                    <Text variant="caption" weight="semibold">
                      {name}
                    </Text>
                    <Text variant="caption" tone="secondary">
                      {detail}
                    </Text>
                  </Stack>
                  <Text variant="caption" weight="semibold" numeric>
                    ${price} a night
                  </Text>
                </Link>
              ))}
            </Stack>
          </Card>
        </Stack>

        <Stack gap={3} className={checkout.summary}>
          <Card tone="dark">
            <Card.Header>
              <Text variant="eyebrow" weight="semibold" tone="onDarkMuted">
                Total for {DEFAULT_NIGHTS} nights
              </Text>
              <Pill tone="accent">3% back</Pill>
            </Card.Header>
            <Text as="p" variant="display" weight="bold" tone="accent" numeric className="pb-mt-3">
              {formatMoney(total)}
            </Text>
            <div className={checkout.rule} />
            {[
              [
                `${formatMoney(fromMajor(nightly, 'USD'))} × ${DEFAULT_NIGHTS} nights`,
                formatMoney(total),
              ],
              ['Booking fee', 'None'],
              ['FX fee', 'None · charged in USD'],
              ['Cashback after check-out', `+${formatMoney(cashback)}`],
            ].map(([label, value], index) => (
              <div key={label} className={checkout.line}>
                <Text variant="caption" tone="onDarkMuted">
                  {label}
                </Text>
                <Text
                  variant="caption"
                  weight="semibold"
                  tone={index === 3 ? 'accent' : 'onDark'}
                  numeric
                >
                  {value}
                </Text>
              </div>
            ))}
            <Card.Footer>
              <Button size="lg" block iconEnd="arrow_forward" to={`/travel/hotels/${stay.slug}/book`}>
                Book with your {BRAND.name} card
              </Button>
              <Stack direction="row" align="center" gap={2} className="pb-mt-3">
                <Icon name="event_available" size={15} />
                <Text variant="eyebrow" tone="onDarkMuted">
                  Free cancellation until 10 October
                </Text>
              </Stack>
            </Card.Footer>
          </Card>
        </Stack>
      </div>
    </>
  )
}
